import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isSameDay } from "date-fns";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, PickersDay } from "@mui/x-date-pickers";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { trakrefBlue, inputGrey, red, Grey4, Grey3, white } from '../../../color_utils';
import { trakrefFontFamily, isDesktop } from '../../../utils';
import UserInfo from '../../../UserInfo';

const defaultStyles = {
    "& .MuiFormLabel-root": { fontFamily: trakrefFontFamily },
    "& .MuiFilledInput-root": {
        backgroundColor: inputGrey,
    },
    "& .MuiFilledInput-root.Mui-disabled": { backgroundColor: Grey4 },
    "& .MuiFormLabel-root.MuiInputLabel-shrink": {
        textTransform: "uppercase",
        fontFamily: trakrefFontFamily,
    },
    "& .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
        color: "#D51A1A !important",
        fontFamily: trakrefFontFamily,
    },
    "& .MuiInputBase-root.MuiInput-underline.Mui-error:after": {
        borderBottomColor: red
    },
    "& .MuiFilledInput-root:after": {
        borderColor: trakrefBlue,
    },
    "& .MuiFilledInput-root.Mui-error:after": {
        borderColor: red
    },
};

const locales = { 'en-US': "MM/dd/yyyy", 'es-ES': "dd/MM/yyyy", 'fr-FR': "dd/MM/yyyy" };
const format = locales[UserInfo.getCulture()];

const DateInput = ({
    id,
    isRequired,
    date,
    open,
    onOpen,
    onClose,
    onChange,
    label,
    disabled,
    width,
    variant,
    error,
    customHelperText,
    allowPast,
    allowFuture,
    maxDate,
    minDate,
    sx,
    disableDatePickerPortal,
    showTodayButton
}) => {

    const [openPicker, setOpenPicker] = useState(false);
    const [dateValidationError, setDateValidationError] = useState("");

    const determineHelperText = () => {
        if (dateValidationError) return dateValidationError;
        return error ? customHelperText || "This field is required" : "";
    };

    const handleOnOpen = () => {
        if (showTodayButton) return;
        setOpenPicker(true);
        onOpen && onOpen();
    };

    const handleIconOpen = () => {
        setOpenPicker(true);
        onOpen && onOpen();
    };

    const handleTodayClick = () => {
        handleOnChange(new Date());
    };

    const handleOnClose = () => {
        // Set date to today on mobile/tablet
        // when clicking "ok" if date is not selected
        if (!date && !isDesktop) {
            onChange(new Date());
        }
        setOpenPicker(false);
        onClose && onClose();
    };

    const handleOnChange = (newDate) => {
        setOpenPicker(false);
        if (newDate && !isNaN(newDate.getTime())) {
            const dateOnly = new Date(newDate).setHours(0, 0, 0, 0);
            const today = new Date().setHours(0, 0, 0, 0);
            const todayFormatted = new Date().toLocaleDateString(UserInfo.getCulture());
            if (allowFuture === false && dateOnly > today) {
                setDateValidationError(`Date cannot be after current date (${todayFormatted})`);
                return;
            }
            if (allowPast === false && dateOnly < today) {
                setDateValidationError(`Date cannot be before current date (${todayFormatted})`);
                return;
            }
        }
        setDateValidationError("");
        onChange(newDate);
    };

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                setOpenPicker(open);
            }, 200);
        }
    }, []);

    const datePicker = (
        <DatePicker
            open={openPicker}
            disablePast={allowPast === false}
            maxDate={allowFuture === false ? maxDate : null}
            minDate={minDate}
            disabled={disabled}
            format={format}
            inputFormat={format}
            label={label + (isRequired ? "*" : "")}
            value={date && new Date(date)}
            defaultValue={null}
            slots={{}}
            slotProps={{
                textField: {
                    id,
                    variant,
                    error: error || !!dateValidationError,
                    disabled,
                    helperText: determineHelperText(),
                    sx: { ...defaultStyles, ...sx, width: '100%' },
                    autoFocus: false,
                    InputProps: {
                        onClick: () => handleOnOpen(),
                        sx: {
                            "& input::placeholder": {
                                paddingLeft: 0,
                                color: Grey3,
                                fontFamily: trakrefFontFamily,
                                opacity: 1,
                                fontSize: "16px",
                            },
                            "& .MuiInputBase-input": {
                                textAlign: "left",
                                fontFamily: trakrefFontFamily,
                                fontSize: "16px"
                            },
                            svg: {
                                color: error ? red : trakrefBlue,
                                opacity: disabled ? "40%" : "100%",
                            },
                        },
                    }
                },
                ...(showTodayButton && {
                    openPickerButton: {
                        onClick: (e) => {
                            e.stopPropagation();
                            handleIconOpen();
                        }
                    }
                })
            }}
            onOpen={showTodayButton ? handleIconOpen : handleOnOpen}
            onClose={handleOnClose}
            onChange={handleOnChange}
        />
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {showTodayButton ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: `${width}%` }}>
                    <Box sx={{ flex: 1 }}>
                        {datePicker}
                    </Box>
                    <Button
                        disabled={disabled}
                        onClick={handleTodayClick}
                        sx={{
                            minWidth: 'auto',
                            fontFamily: trakrefFontFamily,
                            fontWeight: 700,
                            fontSize: '14px',
                            color: trakrefBlue,
                            letterSpacing: '0.46px',
                            whiteSpace: 'nowrap',
                            padding: '16px 12px',
                            '&:hover': {
                                backgroundColor: 'rgba(0,133,155,0.08)',
                            },
                        }}
                    >
                        TODAY
                    </Button>
                </Box>
            ) : (
                <Box sx={{ width: `${width}%` }}>
                    {datePicker}
                </Box>
            )}
        </LocalizationProvider>
    );
};

DateInput.propTypes = {
    isRequired: PropTypes.bool,
    open: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    date: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    width: PropTypes.number,
    variant: PropTypes.string,
    error: PropTypes.bool,
    customHelperText: PropTypes.string,
    maxDate: PropTypes.object,
    minDate: PropTypes.object,
    sx: PropTypes.object,
    id: PropTypes.string,
    allowFuture: PropTypes.bool,
    showTodayButton: PropTypes.bool,
};

DateInput.defaultProps = {
    isRequired: false,
    open: false,
    onOpen: () => { },
    onClose: () => { },
    date: null,
    disabled: false,
    width: 100,
    variant: "outlined",
    error: false,
    customHelperText: "",
    allowPast: false,
    maxDate: new Date(),
    minDate: null,
    sx: {},
    id: "",
    allowFuture: false,
    disableDatePickerPortal: false,
    showTodayButton: false
};

export default DateInput;
